import {FC, useCallback, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {Stack} from "@fluentui/react";
import {LabelComposition, LabelDefinition, LabelDefinitionLocation, LabelDefinitionLocationEnum,} from "../API";
import {ITenantResourceQuery} from "../interfaces/ITenantResourceQuery";
import {TenantList} from "./TenantList";
import {TenantResource} from "../enums/TenantResource";
import {MakeTenantColumns} from "../helpers/MakeTenantColumns";
import {
    ButtonElement,
    CMEditorElement,
    CustomSearchBoxElement,
    ITenantFormLayout,
    TextFieldElement,
} from "./OnixElements";
import {itemInWarehouseOverviewContext} from "./DocumentTypeArbitraryFields";
import {DrawLabelDefinition} from "../pages/LabelDefinitionsPage";

interface ICompanyLabelCompositionsSectionProps {
    companyId: number;
}

export const CompanyLabelCompositionsSection: FC<
    ICompanyLabelCompositionsSectionProps
> = ({companyId}) => {
    const {t} = useTranslation();

    const query = useMemo<ITenantResourceQuery<LabelComposition>>(() => {
        const query: ITenantResourceQuery<LabelComposition> = {};

        if (companyId !== undefined) {
            query.companySettingsId = {
                Equals: companyId,
            };
        }

        return query;
    }, [companyId]);

    const baseDto = useMemo<Partial<LabelComposition>>(
        () => ({
            companySettingsId: companyId,
        }),
        [companyId],
    );

    const columns = useMemo(
        () =>
            MakeTenantColumns<LabelComposition>(
                {
                    name: {
                        minWidth: 300,
                    },
                    svgTemplate: {},
                },
                t,
            ),
        [t],
    );

    const form = useMemo<ITenantFormLayout<LabelComposition>>(
        () => ({
            name: {
                type: new TextFieldElement({}),
            },
            labelDefinitionLocation: {
                type: new CustomSearchBoxElement<
                    LabelDefinitionLocation,
                    LabelComposition
                >({
                    customSearchBoxType: "DEFAULT_CRUD",
                    titleKey: "labelDefinition.name" as any,
                    makeSubtitle: (dto) =>
                        `${dto?.labelDefinition?.manufacturer} - ${dto?.labelDefinition?.name} (${t(LabelDefinitionLocationEnum[dto?.location])})`,
                    resource: TenantResource.LabelDefinitionLocations,
                    key: "id",
                    query: {
                        companySettingsId: {
                            Equals: companyId,
                        },
                    },
                }),
                translationKey:
                    "LabelDefinitionLocation-only-for-LabelComposition-preview",
            },
            svgTemplate: {
                type: new CMEditorElement({
                    tsDefinitionsFromDto: (dto) => {
                        const location = (dto as any)?.labelDefinitionLocationstorage
                            ?.location;

                        return location !== undefined
                            ? {
                                [LabelDefinitionLocationEnum.WarehouseOverview]:
                                itemInWarehouseOverviewContext,
                            }[location as LabelDefinitionLocationEnum]
                            : undefined;
                    },
                    codeLanguage: "javascript",
                    minHeight: 500,
                }),
            },
            addSVGTemplate: {
                type: new ButtonElement({
                    onClick: async (dto, setDto) => {
                        if (setDto) {
                            setDto({
                                ...(dto ?? {}),
                                svgTemplate: `\`
<svg width="100" height="100" xmlns="http://www.w3.org/2000/svg">
    <text x="10" y="10" font-size="4">Predložak</text>
</svg>
\`;`,
                            } as any);
                        }
                    },
                    text: t("addSVGTemplate"),
                }),
            },
        }),
        [companyId, t],
    );

    return (
        <TenantList<LabelComposition, LabelComposition, LabelComposition>
            query={query}
            resource={TenantResource.LabelComposition}
            queryIsRequired
            baseDto={baseDto}
            title={"LabelCompositions"}
            columns={columns}
            form={form}
            addingTitle="AddingLabelComposition"
            editingTitle="EditingLabelComposition"
            minFormWidth={"40vw"}
            formChildren={(dto) => (
                <DrawSVGTemplate
                    svgTemplate={(dto as any)?.svgTemplate}
                    labelDefinition={
                        (dto as any)?.labelDefinitionLocationstorage?.labelDefinition
                    }
                    labelDefinitionLocation={(dto as any)?.labelDefinitionLocationstorage}
                />
            )}
        />
    );
};

function loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });
}

interface IDrawSVGTemplate {
    svgTemplate: string;
    labelDefinition: LabelDefinition;
    labelDefinitionLocation: LabelDefinitionLocation;
}

const DrawSVGTemplate: FC<IDrawSVGTemplate> = ({
                                                   svgTemplate,
                                                   labelDefinition,
                                                   labelDefinitionLocation,
                                               }) => {
    const [svgImage, setSvgImage] = useState<HTMLImageElement>();

    const loadSvgImage = useCallback(async () => {
        if (svgTemplate) {
            switch (labelDefinitionLocation.location) {
                case LabelDefinitionLocationEnum.WarehouseOverview:
                    const fullSvgTemplate = `
                    const ctx = {
                        item: ${JSON.stringify({
                        acIdent: "1234567890",
                    })},
                        definition: ${JSON.stringify(labelDefinition)}
                    };
                    const locale = "hr-HR";
                    
                    ${svgTemplate}
                    `;

                    const evaluatedSvg = eval(fullSvgTemplate).trim();

                    console.log(evaluatedSvg);

                    // Construct a Blob object from SVG data
                    const blob = new Blob([evaluatedSvg], {type: "image/svg+xml"});

                    // Create a new Image object
                    const img = new Image();

                    // Once the image has been loaded...
                    img.onload = function () {
                        setSvgImage(img);

                        // Revoke the blob URL
                        URL.revokeObjectURL(img.src);
                    };

                    img.onerror = function (e) {
                        console.error(e);
                    };

                    // Create a blob URL from the blob
                    img.src = URL.createObjectURL(blob);

                    break;
            }
        }
    }, [svgTemplate, labelDefinition, labelDefinitionLocation]);

    useEffect(() => {
        if (svgTemplate && labelDefinition && labelDefinitionLocation) {
            loadSvgImage();
        }
    }, [svgTemplate, labelDefinition, labelDefinitionLocation, loadSvgImage]);

    return (
        <Stack>
            {labelDefinition && (
                <DrawLabelDefinition
                    labelDefinition={labelDefinition}
                    itemSvgImage={svgImage}
                />
            )}
        </Stack>
    );
};
